import React, { useEffect, useState } from 'react';
import { PhoneIcon, MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { PHONE_DISPLAY, PHONE_TEL, CTAButton, LOGO_URL } from './shared';
// 02 — STICKY HEADER · [Journey] [Conversion]
// Max six top-level items. Logo left, nav centred/right, visible phone, high-contrast CTA.
// Accessible mega-menu for Claims. Mobile menu + persistent bottom action bar.
const claimsMenu = [{
  label: 'Work injuries',
  href: '/workers-compensation-lawyers-perth/'
}, {
  label: 'Road accidents',
  href: '/car-accident-lawyers-perth/'
}, {
  label: 'Public liability',
  href: '/public-liability-lawyers-perth/'
}, {
  label: 'Serious & catastrophic injuries',
  href: '/catastrophic-injury-lawyers-perth/'
}, {
  label: 'Other or unsure',
  href: '#claim-router'
}];
const navItems = [{
  label: 'How It Works',
  href: '#how-it-works'
}, {
  label: 'Results & Reviews',
  href: '#reviews'
}, {
  label: 'Our Team',
  href: '#team'
}, {
  label: 'Resources',
  href: '/resources/'
}];
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [claimsOpen, setClaimsOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <>
      <header className={`sticky top-0 z-40 w-full bg-stone transition-shadow ${scrolled ? 'shadow-[0_1px_0_rgba(27,30,29,0.1)]' : ''}`} data-id="element-174">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-5 py-3.5 sm:px-6 lg:px-8" data-id="element-175">
          <a href="/" className="flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre rounded-btn" data-id="element-176">
            <img src={LOGO_URL} alt="Separovic Injury Lawyers" width={180} height={40} className="h-9 w-auto" data-id="element-177" />
          </a>

          {/* Desktop nav */}
          <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex" data-id="element-178">
            <div className="relative" onMouseEnter={() => setClaimsOpen(true)} onMouseLeave={() => setClaimsOpen(false)} data-id="element-179">
              <button type="button" aria-expanded={claimsOpen} aria-haspopup="true" onClick={() => setClaimsOpen(v => !v)} className="inline-flex items-center gap-1 rounded-btn px-3 py-2 text-body font-medium text-charcoal hover:text-eucalypt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre" data-id="element-180">
                Claims
                <ChevronDownIcon aria-hidden="true" className={`h-4 w-4 transition-transform ${claimsOpen ? 'rotate-180' : ''}`} strokeWidth={1.75} data-id="element-181" />
              </button>
              {claimsOpen && <div className="absolute left-0 top-full w-72 rounded-card border border-charcoal/10 bg-stone p-2 shadow-lg" data-id="element-182">
                  {claimsMenu.map(item => <a key={item.label} href={item.href} className="block rounded-btn px-3 py-2.5 text-body text-charcoal hover:bg-eucalypt/5 hover:text-eucalypt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre" data-id="element-183">
                      {item.label}
                    </a>)}
                </div>}
            </div>
            {navItems.map(item => <a key={item.label} href={item.href} className="rounded-btn px-3 py-2 text-body font-medium text-charcoal hover:text-eucalypt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre" data-id="element-184">
                {item.label}
              </a>)}
          </nav>

          <div className="hidden items-center gap-3 lg:flex" data-id="element-185">
            <a href={PHONE_TEL} className="inline-flex items-center gap-2 rounded-btn px-2 py-2 text-body font-semibold text-eucalypt hover:text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre" data-id="element-186">
              <PhoneIcon aria-hidden="true" className="h-4 w-4" strokeWidth={1.75} data-id="element-187" />
              {PHONE_DISPLAY}
            </a>
            <CTAButton href="#claim-router" className="px-5" data-id="element-188">
              Check My Claim
            </CTAButton>
          </div>

          {/* Mobile controls */}
          <div className="flex items-center gap-1 lg:hidden" data-id="element-189">
            <a href={PHONE_TEL} aria-label={`Call ${PHONE_DISPLAY}`} className="inline-flex h-11 w-11 items-center justify-center rounded-btn text-eucalypt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre" data-id="element-190">
              <PhoneIcon aria-hidden="true" className="h-5 w-5" strokeWidth={1.75} data-id="element-191" />
            </a>
            <button type="button" aria-expanded={mobileOpen} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} onClick={() => setMobileOpen(v => !v)} className="inline-flex h-11 w-11 items-center justify-center rounded-btn text-charcoal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ochre" data-id="element-192">
              {mobileOpen ? <XIcon className="h-6 w-6" strokeWidth={1.75} data-id="element-193" /> : <MenuIcon className="h-6 w-6" strokeWidth={1.75} data-id="element-194" />}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileOpen && <nav aria-label="Mobile" className="border-t border-charcoal/10 bg-stone px-5 pb-6 pt-2 lg:hidden" data-id="element-195">
            <p className="px-1 pb-1 pt-3 text-small font-semibold uppercase tracking-wider text-ochre" data-id="element-196">
              Claims
            </p>
            {claimsMenu.map(item => <a key={item.label} href={item.href} className="block rounded-btn px-1 py-2.5 text-body-lg text-charcoal hover:text-eucalypt" data-id="element-197">
                {item.label}
              </a>)}
            <div className="my-2 h-px bg-charcoal/10" data-id="element-198" />
            {navItems.map(item => <a key={item.label} href={item.href} className="block rounded-btn px-1 py-2.5 text-body-lg text-charcoal hover:text-eucalypt" data-id="element-199">
                {item.label}
              </a>)}
          </nav>}
      </header>

      {/* Mobile bottom action bar — persistent, unobtrusive */}
      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 gap-px border-t border-charcoal/10 bg-charcoal/10 lg:hidden" data-id="element-200">
        <a href={PHONE_TEL} className="flex min-h-[52px] items-center justify-center gap-2 bg-stone text-body font-semibold text-eucalypt" data-id="element-201">
          <PhoneIcon aria-hidden="true" className="h-4 w-4" strokeWidth={1.75} data-id="element-202" />
          Call {PHONE_DISPLAY}
        </a>
        <a href="#claim-router" className="flex min-h-[52px] items-center justify-center gap-2 bg-eucalypt text-body font-semibold text-stone" data-id="element-203">
          Check My Claim
        </a>
      </div>
    </>;
}